/**
 * The structured-value family's surface, as one vocabulary.
 *
 * Every constant carries the `_CLASSES` suffix because that is what the class
 * law (U19) matches: a module-scope string reaching the DOM through a `cn` in
 * another file is just an identifier at the call site, and an unsuffixed name
 * would put the whole family outside the U7/W3 rules meant to govern it.
 *
 * Two things are deliberately absent. There is **no height, width or min/max
 * dimension anywhere** — the field surface (`fieldVariants` /
 * `fieldBlockVariants`) writes the only sizes in the kit, and a spec asserts
 * that no constant here matches a dimension class. And there is no RTL branch:
 * every class below is logical (`ps-`/`text-start`/`border-s`) or axis-neutral.
 * The one physical thing in the family — the JSON gutter sitting on the left —
 * comes from an `dir="ltr"` island, not from a direction-branching class.
 */

/** The column every structured control lays itself out in. */
export const STRUCTURED_SHELL_CLASSES = 'flex flex-col gap-sm';

/** The action row above a control: wraps at 320px rather than overflowing. */
export const STRUCTURED_TOOLBAR_CLASSES = 'flex flex-wrap items-center justify-end gap-sm';

/**
 * The read-only banner. Visible, not only announced: the field is deliberately
 * NOT greyed out (it stays focusable and copyable), so without a line saying so
 * an operator has no way to know why their typing does nothing.
 */
export const STRUCTURED_READ_ONLY_CLASSES =
  'flex items-center gap-sm rounded-md bg-muted px-md py-sm text-caption text-muted-foreground';

/** A row of the issue list. */
export const STRUCTURED_ISSUE_LIST_CLASSES = 'flex flex-col gap-xs';

export const STRUCTURED_ISSUE_ROW_CLASSES = 'flex flex-wrap items-baseline gap-sm text-caption';

export const STRUCTURED_ISSUE_PATH_CLASSES = 'font-mono text-code text-muted-foreground';

export const STRUCTURED_ISSUE_ERROR_CLASSES = 'text-destructive';

export const STRUCTURED_ISSUE_WARNING_CLASSES = 'text-warning';

/* -------------------------------------------------------------------------- */
/*  KeyValueEditor                                                            */
/* -------------------------------------------------------------------------- */

/** The rows, as a list. */
export const KEY_VALUE_LIST_CLASSES = 'flex flex-col gap-sm';

/**
 * One row.
 *
 * A GRID rather than a flex line, and stacked below `sm`. Two 50/50 flex
 * children at 320px gave a key box and a value box roughly nine characters wide
 * each; the grid collapses to one column there, where each cell gets the full
 * width and its own visible label.
 */
export const KEY_VALUE_ROW_CLASSES = 'grid grid-cols-1 items-start gap-sm sm:gap-md';

export const KEY_VALUE_ROW_COLUMN_CLASSES = {
  balanced: 'sm:grid-cols-kv-balanced',
  'key-narrow': 'sm:grid-cols-kv-key-narrow',
  'key-wide': 'sm:grid-cols-kv-key-wide',
};

/** A cell's own column, so a per-row error sits under the control it belongs to. */
export const KEY_VALUE_CELL_CLASSES = 'flex min-w-0 flex-col gap-xs';

/**
 * A catalogue key, rendered as text instead of an input.
 *
 * `break-words` rather than `truncate`: a key an operator cannot read in full is
 * the same failure as a key they could mistype, and there is nothing below this
 * row competing for the space.
 */
export const KEY_VALUE_KEY_LABEL_CLASSES =
  'flex min-w-0 flex-col gap-2xs break-words pt-2xs text-label text-foreground';

export const KEY_VALUE_KEY_DESCRIPTION_CLASSES = 'text-caption font-normal text-muted-foreground';

/** A key an operator types: an identifier, so mono and always LTR. */
export const KEY_VALUE_KEY_INPUT_CLASSES = 'font-mono';

/** The label a stacked row shows above each cell, hidden once the grid splits. */
export const KEY_VALUE_CELL_LABEL_CLASSES = 'text-caption text-muted-foreground sm:sr-only';

/** The actions column: as wide as its buttons, and never a negotiating flex item. */
export const KEY_VALUE_ACTIONS_CLASSES = 'flex shrink-0 items-center gap-xs sm:pt-2xs';

/** A boolean row's on/off word, so the switch is not the only thing saying it. */
export const KEY_VALUE_SWITCH_ROW_CLASSES = 'flex items-center gap-sm';

export const KEY_VALUE_SWITCH_STATE_CLASSES = 'text-caption text-muted-foreground';

/** A per-row problem — a caller's issue, a duplicate key, a value that is not a number. */
export const KEY_VALUE_ROW_ERROR_CLASSES = 'text-caption text-destructive';

export const KEY_VALUE_ROW_WARNING_CLASSES = 'text-caption text-warning';

/** A value whose stored type contradicts its declared kind, shown verbatim. */
export const KEY_VALUE_RAW_VALUE_CLASSES =
  'min-w-0 break-words rounded-md bg-muted px-md py-sm font-mono text-code text-foreground';

export const KEY_VALUE_EMPTY_CLASSES = 'text-caption text-muted-foreground';

export const KEY_VALUE_FOOTER_CLASSES = 'flex flex-wrap items-center gap-sm';

/* -------------------------------------------------------------------------- */
/*  JsonEditor                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The editor island: the gutter and the textarea, as one bordered box.
 *
 * It carries `dir="ltr"` — JSON is code — while everything around it (label,
 * toolbar, errors, issues) stays in the interface direction. That is why the
 * gutter lands on the physical left in Arabic as well as English, and why no
 * class here needs a direction branch to make it happen.
 */
export const JSON_ISLAND_CLASSES =
  'flex overflow-hidden rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background';

export const JSON_ISLAND_INVALID_CLASSES = 'border-destructive focus-within:ring-destructive';

/**
 * The line-number column. `select-none` so dragging across the code does not
 * copy the numbers with it, and `overflow-hidden` because it is scrolled by the
 * textarea rather than by itself.
 */
export const JSON_GUTTER_CLASSES =
  'min-w-code-gutter shrink-0 select-none overflow-hidden border-e border-border bg-muted py-sm text-end font-mono text-code text-muted-foreground';

export const JSON_GUTTER_LINE_CLASSES = 'block px-sm';

/** The line the parser stopped on. */
export const JSON_GUTTER_LINE_ERROR_CLASSES = 'bg-destructive/10 font-medium text-destructive';

/**
 * The code surface.
 *
 * The border and the ring live on the island, so this is deliberately borderless
 * and ring-less: two focus rings around one control is worse than none.
 * `resize-none` because the height is the line count — a dragged height and an
 * auto-growing one contradict each other, and the toolbar's expand toggle is the
 * keyboard-reachable way to say the same thing.
 */
export const JSON_TEXTAREA_CLASSES =
  'grow border-0 bg-transparent font-mono text-code focus-visible:ring-0 focus-visible:ring-offset-0';

export const JSON_MESSAGE_CLASSES = 'flex flex-wrap items-baseline gap-sm text-caption';

export const JSON_HINT_CLASSES = 'text-caption text-muted-foreground';

/* -------------------------------------------------------------------------- */
/*  MarkdownEditor                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The editor island. Unlike the JSON island it carries NO `dir`: a legal body
 * reads in the direction of its own locale, which the caller passes in.
 */
export const MARKDOWN_ISLAND_CLASSES =
  'flex overflow-hidden rounded-md border border-input bg-card focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background';

export const MARKDOWN_ISLAND_INVALID_CLASSES = 'border-destructive focus-within:ring-destructive';

/** A div has no `disabled:` pseudo-class, so the field surface's disabled look is spelled out. */
export const MARKDOWN_ISLAND_DISABLED_CLASSES = 'cursor-not-allowed bg-muted opacity-60';

/**
 * The prose surface — the document in the product's own type scale, one step
 * down from the page (h1 → `h2`) exactly as the mobile reader draws it, so what
 * staff see is what the member sees. Descendant variants because ProseMirror
 * owns those elements; logical padding so list markers follow the direction.
 */
export const MARKDOWN_PROSE_CLASSES =
  'grow px-md py-sm text-body text-foreground focus:outline-none [&_h1]:text-h2 [&_h2]:text-h3 [&_h3]:text-h4 [&_h1]:mt-md [&_h2]:mt-md [&_h3]:mt-sm [&_p]:my-xs [&_ul]:my-xs [&_ol]:my-xs [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ps-lg [&_ol]:ps-lg [&_li]:my-2xs [&_strong]:font-semibold';

/** A toolbar toggle that is on: the state is on `aria-pressed`, so the style hangs there too. */
export const MARKDOWN_TOOL_CLASSES = 'aria-pressed:bg-secondary aria-pressed:text-primary';

export const MARKDOWN_HINT_CLASSES = 'text-caption text-muted-foreground';

export const MARKDOWN_WARNING_CLASSES = 'text-caption text-warning';
